/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { ListeTable } from '@mairistem/liste';

import { EllipsisText } from '@/components/Common';

import {
  formatDateHeureListe,
  formatDateSeule,
  formatNumeroActeListe,
} from '@/utils';

interface ActeMariageListeTableRowProps {
  item: any;
  selected?: boolean;
  /** Réduit la ligne à la seule colonne du nom, pour la sidebar de la fiche. */
  sidebar?: boolean;
}

/**
 * Ligne de la liste des mariages : sept colonnes, dont DEUX personnes — les deux
 * conjoints. C'est la seule liste à en afficher deux.
 *
 * ⚠️ Les parents ne sont pas affichés, alors que la projection les remonte
 * (`nom_classement_pere`, `…_mere`, et la paire `…_2` propre au mariage). Les
 * colonnes restent disponibles côté API si la liste doit les reprendre.
 */
const ActeMariageListeTableRow = (props: ActeMariageListeTableRowProps) => {
  const { item, selected, sidebar } = props;

  if (!item) return null;

  const annee = item?.annee;
  const numeroRegistre = item?.numero_registre;
  const libelleRegistre = item?.libelle_registre;
  const libelleTypeActe = item?.libelle_type_acte;
  const dateActe = item?.date_acte;
  const heureActe = item?.heure_acte;
  const minuteActe = item?.minute_acte;
  const dateMariage = item?.date_mariage;
  const nomConjoint1 = item?.nom_classement;
  const nomConjoint2 = item?.nom_classement_2;

  const registre =
    annee || numeroRegistre || libelleRegistre
      ? `${annee ?? '-'} - ${numeroRegistre ?? '-'} - ${
          libelleRegistre ? libelleRegistre.toUpperCase() : '-'
        }`
      : '-';

  const acte = formatNumeroActeListe(item);

  const dresse = formatDateHeureListe(dateActe, heureActe, minuteActe);

  if (sidebar) {
    const conjoints = [nomConjoint1, nomConjoint2].filter(Boolean).join(' et ');

    return (
      <ListeTable.Row key={item.identifiant ?? item?.id} selected={selected}>
        <ListeTable.Cell name="nom">
          <EllipsisText text={conjoints || 'Non renseigné'} />
        </ListeTable.Cell>
      </ListeTable.Row>
    );
  }

  return (
    <ListeTable.Row key={item.identifiant ?? item?.id} selected={selected}>
      <ListeTable.Cell name="registre">
        <EllipsisText text={registre} />
      </ListeTable.Cell>
      <ListeTable.Cell name="typeActe">
        <EllipsisText text={libelleTypeActe || '-'} />
      </ListeTable.Cell>
      <ListeTable.Cell name="numero">
        <EllipsisText text={acte} />
      </ListeTable.Cell>
      <ListeTable.Cell name="dresse">
        <EllipsisText text={dresse} />
      </ListeTable.Cell>
      <ListeTable.Cell name="dateMariage">
        {/* Une date SEULE : `am_mg_heure` n'alimente pas cette colonne, l'heure
            de la célébration n'apparaît que sur la fiche. */}
        <EllipsisText text={formatDateSeule(dateMariage) || '-'} />
      </ListeTable.Cell>
      <ListeTable.Cell name="conjoint1">
        <EllipsisText text={nomConjoint1 || 'Non renseigné'} />
      </ListeTable.Cell>
      <ListeTable.Cell name="conjoint2">
        <EllipsisText text={nomConjoint2 || 'Non renseigné'} />
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default ActeMariageListeTableRow;
